// src/services/ThumbnailService.ts
import { generatePDFThumbnail } from './PDFThumbnailService';
import { devLog } from '../utils/devTools';
import { generateWordThumbnail } from './WordThumbnailService';
import { generateTextThumbnail } from './TextThumbnailService';
import { pdfjs } from 'react-pdf';

// Ensure PDF.js worker is configured
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs`;
}

export interface RemoteFileRef {
  url?: string;
  name?: string;
  originalName?: string;
  filename?: string;
  type?: string;
  mimetype?: string;
  size?: number;
}

interface NormalizedFile {
  name: string;
  mimeType: string;
  size: number;
  url?: string;
  blob?: File;
}

function normalize(input: File | RemoteFileRef): NormalizedFile {
  if (input instanceof File) {
    return { name: input.name, mimeType: input.type, size: input.size, blob: input };
  }
  return {
    name: input.originalName ?? input.filename ?? input.name ?? '',
    mimeType: input.mimetype ?? input.type ?? '',
    size: input.size ?? 0,
    url: input.url,
  };
}

/**
 * Main entry point for generating document thumbnails
 * Handles routing to specific thumbnail generators based on file type
 * @param input File to generate thumbnail for
 * @returns Promise resolving to thumbnail data URL
 */
export async function generateThumbnail(input?: File | RemoteFileRef | null): Promise<string> {
  if (!input) {
    console.error('generateThumbnail received null or undefined file');
    return '/file-icons/generic.svg';
  }

  const file = normalize(input);
  const { name: fileName, mimeType: fileType, blob, url } = file;

  // Log file details for debugging
  devLog('ThumbnailService: generating thumbnail for file:', {
    isFile: !!blob,
    name: fileName,
    type: fileType,
    size: file.size,
    hasUrl: !!url,
  });

  try {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const isImage = fileType && fileType.startsWith('image/');

    // HANDLE NON-IMAGE FILES
    if (!isImage) {
      // PDF Files
      if (extension === 'pdf' || fileType === 'application/pdf') {
        devLog('ThumbnailService: Generating PDF thumbnail');
        if (blob) {
          try {
            // Use react-pdf via our PDFThumbnailService
            return await generatePDFThumbnail(blob);
          } catch (err) {
            console.error('PDF thumbnail generation failed:', err);
            return '/file-icons/pdf.svg';
          }
        } else if (url) {
          try {
            // For remote files with URLs, we can also use the PDF service
            const response = await fetch(url);
            const fetchedBlob = await response.blob();
            const pdfFile = new File([fetchedBlob], fileName || 'document.pdf', { type: 'application/pdf' });
            return await generatePDFThumbnail(pdfFile);
          } catch (err) {
            console.error('PDF thumbnail generation from URL failed:', err);
            return '/file-icons/pdf.svg';
          }
        } else {
          return '/file-icons/pdf.svg';
        }
      }

      // Word Documents
      if (['doc', 'docx'].includes(extension) || fileType.includes('word')) {
        devLog('ThumbnailService: Generating Word thumbnail');
        if (blob) {
          try {
            return await generateWordThumbnail(blob);
          } catch (err) {
            console.error('Word thumbnail generation failed:', err);
            return '/file-icons/word.svg';
          }
        } else {
          return '/file-icons/word.svg';
        }
      }

      // Text Files
      if (['txt', 'csv'].includes(extension) || fileType.includes('text')) {
        devLog('ThumbnailService: Generating Text thumbnail');
        if (blob) {
          try {
            return await generateTextThumbnail(blob);
          } catch (err) {
            console.error('Text thumbnail generation failed:', err);
            return '/file-icons/text.svg';
          }
        } else {
          return '/file-icons/text.svg';
        }
      }

      // Excel Files
      if (['xls', 'xlsx'].includes(extension) || fileType.includes('excel')) {
        return '/file-icons/excel.svg';
      }

      // PowerPoint Files
      if (['ppt', 'pptx'].includes(extension) || fileType.includes('powerpoint')) {
        return '/file-icons/powerpoint.svg';
      }

      // Default for other file types
      return '/file-icons/generic.svg';
    }

    // HANDLE IMAGE FILES
    // For images, we can use the existing implementation from useFileUpload.ts
    // Case 1: We have a Blob/File object
    if (blob) {
      return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const img = new Image();

            img.onload = () => {
              try {
                // Create thumbnail canvas
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                  resolve('/file-icons/generic.svg');
                  return;
                }

                // Calculate thumbnail size
                const MAX_WIDTH = 124;
                const MAX_HEIGHT = 144;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                  if (width > MAX_WIDTH) {
                    height = Math.round(height * (MAX_WIDTH / width));
                    width = MAX_WIDTH;
                  }
                } else {
                  if (height > MAX_HEIGHT) {
                    width = Math.round(width * (MAX_HEIGHT / height));
                    height = MAX_HEIGHT;
                  }
                }

                // Draw the image
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                resolve(canvas.toDataURL('image/jpeg', 0.7));
              } catch (err) {
                console.error('Error creating image thumbnail:', err);
                resolve('/file-icons/generic.svg');
              }
            };

            img.onerror = () => {
              console.error('Error loading image from data URL');
              resolve('/file-icons/generic.svg');
            };

            img.src = e.target?.result as string;
          } catch (err) {
            console.error('Error processing loaded image data:', err);
            resolve('/file-icons/generic.svg');
          }
        };

        reader.onerror = () => {
          console.error('Error reading file with FileReader');
          resolve('/file-icons/generic.svg');
        };

        reader.readAsDataURL(blob);
      });
    }

    // Case 2: We have a URL
    if (url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous'; // For CORS

        img.onload = () => {
          try {
            // Create thumbnail canvas
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              resolve('/file-icons/generic.svg');
              return;
            }

            // Calculate thumbnail size
            const MAX_WIDTH = 120;
            const MAX_HEIGHT = 140;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height = Math.round(height * (MAX_WIDTH / width));
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width = Math.round(width * (MAX_HEIGHT / height));
                height = MAX_HEIGHT;
              }
            }

            // Draw the image
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            try {
              const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
              resolve(thumbnailUrl);
            } catch (err) {
              // CORS issue with canvas.toDataURL - use original URL
              console.error('CORS issue with canvas.toDataURL:', err);
              resolve(url);
            }
          } catch (err) {
            console.error('Error creating thumbnail from URL:', err);
            resolve('/file-icons/generic.svg');
          }
        };

        img.onerror = () => {
          console.error('Failed to load image from URL:', url);
          resolve('/file-icons/generic.svg');
        };

        img.src = url;
      });
    }

    // Fallback to generic icon
    return '/file-icons/generic.svg';
  } catch (error) {
    console.error('Error in thumbnail generation:', error);
    return '/file-icons/generic.svg';
  }
}